// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
// Editor tabular dos lançamentos do S-26 (52 linhas) com totais automáticos.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Plus, ArrowDownUp, AlertTriangle, BookMarked } from 'lucide-react';
import { S26_ROW_FIELDS, computeS26LedgerTotals, computeS26SymbolWarnings, formatBR, maskBRL, sortS26Rows } from './s26Sync';
import S26DescriptionsDialog from './audit/S26DescriptionsDialog';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export { S26_ROW_FIELDS };

export function S26LedgerTable({ values, onChange }) {
  const { user, isAdmin } = useAuth();
  const canManageDescs = isAdmin() || ['servo_contas', 'secretario'].includes(user?.role);
  const [showDescsDialog, setShowDescsDialog] = useState(false);

  const lastFilledRow = useMemo(() => {
    for (let n = 52; n >= 1; n--) {
      const f = S26_ROW_FIELDS(n);
      if (Object.values(f).some((name) => (values[name] || '').toString().trim())) return n;
    }
    return 0;
  }, [values]);

  const [visibleRows, setVisibleRows] = useState(() => Math.min(52, Math.max(12, lastFilledRow + 3)));

  useEffect(() => {
    setVisibleRows((v) => Math.min(52, Math.max(v, lastFilledRow + 3)));
  }, [lastFilledRow]);

  const totals = useMemo(() => computeS26LedgerTotals(values), [values]);

  // Avisa quando o símbolo não combina com o lado do valor (D/J = Entrada, G = Saída).
  const symbolWarnings = useMemo(() => computeS26SymbolWarnings(values), [values]);

  // Descrições já usadas (nesta folha e nas anteriores) para sugestão ao digitar.
  const [savedDescs, setSavedDescs] = useState([]);
  const [savedSymbols, setSavedSymbols] = useState({});
  const fetchDescs = () => {
    axios.get(`${API}/audit-forms/s26/descriptions`)
      .then(({ data }) => { setSavedDescs(data.descriptions || []); setSavedSymbols(data.symbols || {}); })
      .catch(() => {});
  };
  useEffect(fetchDescs, []);

  // Padrão descrição → símbolo (folha atual tem prioridade sobre o histórico).
  const descSymbols = useMemo(() => {
    const map = { ...savedSymbols };
    for (let n = 1; n <= 52; n++) {
      const f = S26_ROW_FIELDS(n);
      const d = (values[f.desc] || '').trim().toUpperCase();
      const s = (values[f.simbolo] || '').trim().toUpperCase();
      if (d && ['D', 'J', 'G', 'A', 'R'].includes(s)) map[d] = s;
    }
    return map;
  }, [savedSymbols, values]);

  const suggestions = useMemo(() => {
    const set = new Set(savedDescs);
    for (let n = 1; n <= 52; n++) {
      const d = (values[S26_ROW_FIELDS(n).desc] || '').trim();
      if (d) set.add(d.toUpperCase());
    }
    return [...set].sort();
  }, [savedDescs, values]);

  const cell = (name, opts = {}) => (
    <input
      value={values[name] || ''}
      onChange={(e) => {
        const val = opts.money ? maskBRL(e.target.value) : e.target.value;
        onChange(name, val);
        opts.onAfterChange?.(val);
      }}
      data-testid={`s26-cell-${name}`}
      placeholder={opts.placeholder || ''}
      inputMode={opts.money ? 'numeric' : undefined}
      list={opts.list}
      className={`w-full bg-transparent border border-border/60 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary ${opts.className || ''}`}
    />
  );

  return (
    <div className="space-y-3" data-testid="s26-ledger-table">
      <datalist id="s26-desc-suggestions" data-testid="s26-desc-suggestions">
        {suggestions.map((d) => <option key={d} value={d} />)}
      </datalist>
      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="w-full text-xs" style={{ minWidth: 900 }}>
          <thead className="sticky top-0 bg-card z-10">
            <tr className="text-muted-foreground">
              <th rowSpan={2} className="px-1 py-1.5 border-b border-r border-border w-8">#</th>
              <th rowSpan={2} className="px-1 py-1.5 border-b border-r border-border w-14">Data</th>
              <th rowSpan={2} className="px-1 py-1.5 border-b border-r border-border text-left">Descrição da movimentação</th>
              <th rowSpan={2} className="px-1 py-1.5 border-b border-r border-border w-16">S</th>
              <th colSpan={2} className="px-1 py-1 border-b border-r border-border bg-emerald-500/10">Donativos</th>
              <th colSpan={2} className="px-1 py-1 border-b border-r border-border bg-amber-500/10">Conta bancária/Cofre</th>
              <th colSpan={2} className="px-1 py-1 border-b border-border bg-indigo-500/10">Outra</th>
            </tr>
            <tr className="text-muted-foreground">
              <th className="px-1 py-1 border-b border-border w-20 bg-emerald-500/10">Entrada</th>
              <th className="px-1 py-1 border-b border-r border-border w-20 bg-emerald-500/10">Saída</th>
              <th className="px-1 py-1 border-b border-border w-20 bg-amber-500/10">Entrada</th>
              <th className="px-1 py-1 border-b border-r border-border w-20 bg-amber-500/10">Saída</th>
              <th className="px-1 py-1 border-b border-border w-20 bg-indigo-500/10">Entrada</th>
              <th className="px-1 py-1 border-b border-border w-20 bg-indigo-500/10">Saída</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: visibleRows }, (_, i) => i + 1).map((n) => {
              const f = S26_ROW_FIELDS(n);
              return (
                <tr key={n} className="odd:bg-muted/30">
                  <td className="px-1 py-0.5 border-r border-border text-center text-muted-foreground">{n}</td>
                  <td className="px-0.5 py-0.5 border-r border-border">{cell(f.data, { placeholder: 'dd' })}</td>
                  <td className="px-0.5 py-0.5 border-r border-border">{cell(f.desc, {
                    list: 's26-desc-suggestions',
                    onAfterChange: (val) => {
                      const sym = descSymbols[val.trim().toUpperCase()];
                      if (sym && !(values[f.simbolo] || '').trim()) onChange(f.simbolo, sym);
                    },
                  })}</td>
                  <td className="px-0.5 py-0.5 border-r border-border">
                    <select
                      value={values[f.simbolo] || ''}
                      onChange={(e) => onChange(f.simbolo, e.target.value)}
                      data-testid={`s26-cell-${f.simbolo}`}
                      title={symbolWarnings[n] || undefined}
                      className={`w-full bg-transparent border rounded px-0.5 py-1 text-xs focus:outline-none focus:ring-1 ${symbolWarnings[n] ? 'border-red-500 ring-1 ring-red-500 bg-red-500/10 focus:ring-red-500' : 'border-border/60 focus:ring-primary'}`}
                    >
                      <option value=""></option>
                      <option value="D">D</option>
                      <option value="J">J</option>
                      <option value="G">G</option>
                      <option value="A">A</option>
                      <option value="R">R</option>
                    </select>
                  </td>
                  <td className="px-0.5 py-0.5">{cell(f.donE, { placeholder: '0,00', className: 'text-right', money: true })}</td>
                  <td className="px-0.5 py-0.5 border-r border-border">{cell(f.donS, { placeholder: '0,00', className: 'text-right', money: true })}</td>
                  <td className="px-0.5 py-0.5">{cell(f.contaE, { placeholder: '0,00', className: 'text-right', money: true })}</td>
                  <td className="px-0.5 py-0.5 border-r border-border">{cell(f.contaS, { placeholder: '0,00', className: 'text-right', money: true })}</td>
                  <td className="px-0.5 py-0.5">{cell(f.outraE, { placeholder: '0,00', className: 'text-right', money: true })}</td>
                  <td className="px-0.5 py-0.5">{cell(f.outraS, { placeholder: '0,00', className: 'text-right', money: true })}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-muted font-semibold" data-testid="s26-totals-row">
              <td colSpan={4} className="px-2 py-1.5 border-t border-r border-border text-right">TOTAL</td>
              <td className="px-1.5 py-1.5 border-t border-border text-right" data-testid="s26-total-donE">{formatBR(totals.donE)}</td>
              <td className="px-1.5 py-1.5 border-t border-r border-border text-right" data-testid="s26-total-donS">{formatBR(totals.donS)}</td>
              <td className="px-1.5 py-1.5 border-t border-border text-right" data-testid="s26-total-contaE">{formatBR(totals.contaE)}</td>
              <td className="px-1.5 py-1.5 border-t border-r border-border text-right" data-testid="s26-total-contaS">{formatBR(totals.contaS)}</td>
              <td className="px-1.5 py-1.5 border-t border-border text-right" data-testid="s26-total-outraE">{formatBR(totals.outraE)}</td>
              <td className="px-1.5 py-1.5 border-t border-border text-right" data-testid="s26-total-outraS">{formatBR(totals.outraS)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {Object.keys(symbolWarnings).length > 0 && (
        <div className="border border-red-500/40 bg-red-500/5 rounded-lg px-3 py-2 space-y-1" data-testid="s26-symbol-warnings">
          {Object.entries(symbolWarnings).map(([n, msg]) => (
            <p key={n} className="text-xs text-red-600 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Linha {n}: {msg}. Confira o símbolo ou o lado do valor.
            </p>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          S: <strong>D</strong> = Depósito · <strong>J</strong> = Juros recebidos · <strong>G</strong> = Gastos · <strong>A</strong> = Aplicação · <strong>R</strong> = Resgate.
          A/R são movimentações internas e não vão para o S-44.
          Totais e Quadro Resumo (Entrada, Saída, Total e Saldo disponível) calculados automaticamente.
        </p>
        <div className="flex items-center gap-2">
          {canManageDescs && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDescsDialog(true)}
              title="Cadastrar descrições padrão para as sugestões"
              data-testid="s26-descs-btn"
            >
              <BookMarked className="w-3.5 h-3.5 mr-1" /> Descrições
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const updates = sortS26Rows(values);
              if (updates) Object.entries(updates).forEach(([field, val]) => onChange(field, val));
            }}
            title="Reordenar lançamentos em ordem cronológica pelo Dia"
            data-testid="s26-sort-btn"
          >
            <ArrowDownUp className="w-3.5 h-3.5 mr-1" /> Ordenar por dia
          </Button>
          {visibleRows < 52 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVisibleRows((v) => Math.min(52, v + 5))}
              data-testid="s26-add-rows-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Mais linhas ({visibleRows}/52)
            </Button>
          )}
        </div>
      </div>

      <S26DescriptionsDialog
        open={showDescsDialog}
        onOpenChange={setShowDescsDialog}
        onChanged={fetchDescs}
      />
    </div>
  );
}
